<template>
  <div class="container mt-4">
    <TopNav :showOpenMainNav="true">
      <NavItem
        v-if="media"
        icon="far fa-trash-alt"
        :text="$t('GENERAL.BUTTON.DELETE')"
        @click.native="showDeleteModal"
      />
      <NavItem
        v-if="media && showDownloadVideoButton"
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.DOWNLOAD')"
        :loading="isRetrievingDownloadLink"
        @click.native="getDownloadLink"
      />
      <a
        v-if="showDownloadAudioButton"
        :href="media.media.storage_url"
        target="_blank"
      >
        <NavItem icon="far fa-save" :text="$t('GENERAL.BUTTON.DOWNLOAD')" />
      </a>
      <NavItem
        v-if="media"
        icon="far fa-clone"
        :text="$t('GENERAL.BUTTON.DUPLICATE')"
        :loading="isDuplicating"
        @click.native="duplicate"
      />
      <NavItem
        v-if="media"
        icon="far fa-pencil"
        :text="$t('GENERAL.BUTTON.EDIT')"
        @click.native="goToEdit"
      />
      <NavItem
        v-if="media"
        icon="fal fa-plus"
        :text="$t('GENERAL.BUTTON.ADD_TO_PRODUCTS')"
        @click.native="showAddToProduct"
      />
    </TopNav>
    <div v-if="media">
      <LibraryMediaDetails :media="media" class="hm-media-file-preview" />
    </div>
  </div>
</template>

<script>
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import LibraryMediaDetails from '@/web/components/media-library/LibraryMediaDetails.vue';
import LibraryMediaDeleteModal from '@/web/components/media-library/LibraryMediaDeleteModal.vue';
import ProductsSelectModal from '@/web/components/products/ProductsSelectModal';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import moment from 'moment';

export default {
  name: 'LibraryViewMedia',
  components: {
    TopNav,
    NavItem,
    LibraryMediaDetails,
  },
  data: () => ({
    media: null,
    isRetrievingDownloadLink: false,
    isDuplicating: false,
  }),
  computed: {
    ...mapGetters({
      productsCanAddMediaTo: 'product/productsCanAddMediaTo',
      teacher: 'user/teacher',
    }),
    showDownloadVideoButton() {
      if (this.media?.media?.download_link) return true;
      if (!this.teacher.downloads_enabled_until) return false;
      if (moment(this.teacher.downloads_enabled_until).isBefore(moment()))
        return false;

      return (
        this.media &&
        this.media.media &&
        this.media.media.video_url &&
        ['recording', 'vimeo'].indexOf(this.media.media.file_type) !== -1
      );
    },
    showDownloadAudioButton() {
      return (
        this.media &&
        this.media.media &&
        this.media.media.storage_url &&
        this.media.media.file_content === 'audio'
      );
    },
  },
  methods: {
    goToEdit() {
      this.$router.push({
        name: 'media-library-edit-item',
        params: { media_id: this.media.id },
      });
    },
    async getDownloadLink() {
      if (this.isRetrievingDownloadLink) return;

      try {
        let downloadLink = this.media?.media?.download_link;
        if (!downloadLink) {
          this.isRetrievingDownloadLink = true;
          const response = await this.$store.dispatch(
            'mediaLibrary/getVideoDownloadLink',
            this.media?.media?.id
          );
          downloadLink = response.download_link;
        }
        if (downloadLink) {
          const link = document.createElement('a');
          link.href = downloadLink;
          link.click();
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to get the download link',
          });
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to get the download link',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isRetrievingDownloadLink = false;
      }
    },
    async duplicate() {
      if (this.isDuplicating) return;

      try {
        this.isDuplicating = true;
        const duplicatedMedia = await this.$store.dispatch(
          'mediaLibrary/duplicate',
          this.media?.id
        );
        this.$router.push({
          name: 'media-library-edit-item',
          params: { media_id: duplicatedMedia.id },
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to duplicate media',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isDuplicating = false;
      }
    },
    showDeleteModal() {
      this.$modal.show(LibraryMediaDeleteModal, { mediaId: this.media.id });
    },
    async showAddToProduct() {
      this.$modal.show(
        ProductsSelectModal,
        {
          products: this.productsCanAddMediaTo,
        },
        { classes: ['vm--inline-modal'], clickToClose: false },
        {
          'products-selected': async selectedProductIds => {
            try {
              for (const item of selectedProductIds) {
                const res = await this.$store.dispatch(
                  'product/createProductContent',
                  {
                    productId: item,
                    data: { content: [this.media?.id] },
                  }
                );
              }
              this.$notify({
                group: 'app',
                type: 'success',
                title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
                text: this.$t(
                  'MEDIA_LIBRARY.NOTIFICATIONS.MEDIA_ADDED_TO_PRODUCT_SUCCESS'
                ),
              });
            } catch (error) {
              this.$notify({
                group: 'app',
                type: 'error',
                title: this.$t(
                  'MEDIA_LIBRARY.NOTIFICATIONS.MEDIA_ADDED_TO_PRODUCT_FAILURE'
                ),
                text: errorToMessage(
                  error.response,
                  `Request failed: ${error}`
                ),
                duration: -1, // stay ON
              });
            }
          },
        }
      );
    },
  },
  async mounted() {
    await this.$store.dispatch('product/getTeacherProducts');
    this.media = await this.$store.dispatch(
      'mediaLibrary/getMediaById',
      this.$route.params.media_id
    );
  },
};
</script>

<style lang="scss" scoped>
.hm-media-file-preview ::v-deep .embed-responsive-3by4 {
  max-height: 600px !important;
}
</style>
